// @unocss-include
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import classNames, { clsx } from 'clsx';
import { createContext, useCallback, useContext, useEffect, useId, useState, } from 'react';
import { Button } from '../button/index.js';
import { CameraDeviceSelector, CameraFullscreenButton, CameraRoot, CameraShutterButton, } from '../camera/index.js';
import { Icon } from '../icon/index.js';
/**
 * Renders an image if one is already set, and allows either clicking
 * on the image to select a new one, or dragging a new image onto the
 * component to replace the existing one.
 */
export function ImageUploaderRoot(_a) {
    var { value, onChange: handleChange, maxDimension, children } = _a, rest = __rest(_a, ["value", "onChange", "maxDimension", "children"]);
    const inputId = useId();
    const [dragging, setDragging] = useState(false);
    const [draggingOver, setDraggingOver] = useState(false);
    const onDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDraggingOver(true);
    }, []);
    const onDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDraggingOver(false);
    }, []);
    const onDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDraggingOver(true);
    }, []);
    const onChange = useCallback(async (file) => {
        if (!file) {
            handleChange(null);
        }
        else if (maxDimension) {
            let resizer = await import('browser-image-resizer');
            // @ts-ignore
            resizer = resizer.readAndCompressImage ? resizer : resizer.default;
            if (!resizer || !resizer.readAndCompressImage) {
                // fallback. this happens in dev environments using untransformed TS.
                // TODO: fix this.
                console.warn('browser-image-resizer not available, uploading original image');
                handleChange(file);
                return;
            }
            const resizedImage = await resizer.readAndCompressImage(file, {
                maxWidth: maxDimension,
                maxHeight: maxDimension,
                mimeType: file.type,
            });
            handleChange(new File([resizedImage], file.name, { type: file.type }));
        }
        else {
            handleChange(file);
        }
    }, [handleChange, maxDimension]);
    const onDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDraggingOver(false);
        if (e.dataTransfer.files.length > 0) {
            onChange(e.dataTransfer.files[0]);
        }
    }, [onChange]);
    const onDragStart = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    }, []);
    const onDragEnd = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
    }, []);
    const onFileChange = useCallback((e) => {
        if (e.target.files && e.target.files.length > 0) {
            onChange(e.target.files[0]);
        }
    }, [onChange]);
    const onFileClick = useCallback((e) => {
        e.stopPropagation();
    }, []);
    return (_jsx(ImageUploaderContext.Provider, { value: { inputId, dragging, draggingOver, value, onChange }, children: _jsxs("div", { className: classNames('relative rounded-lg', rest.className), onDragEnter: onDragEnter, onDragLeave: onDragLeave, onDragOver: onDragOver, onDrop: onDrop, onDragStart: onDragStart, onDragEnd: onDragEnd, children: [!value && (_jsx("input", { type: "file", accept: "image/*", onChange: onFileChange, onClick: onFileClick, className: "absolute inset-0 pointer-events-none op-0", id: inputId })), children] }) }));
}
const ImageUploaderContext = createContext(null);
function useUploaderContext() {
    const context = useContext(ImageUploaderContext);
    if (!context) {
        throw new Error('ImageUploaderContext not found');
    }
    return context;
}
function ImageUploaderPrebuilt(props) {
    const [cameraOpen, setCameraOpen] = useState(false);
    const openCamera = () => setCameraOpen(true);
    return (_jsxs(ImageUploaderRoot, Object.assign({}, props, { children: [_jsx(ImageUploaderDisplay, {}), _jsxs(ImageUploaderEmptyControls, { children: [_jsx(ImageUploaderFileButton, {}), _jsxs(Button, { color: "ghost", onClick: openCamera, children: [_jsx(Icon, { name: "camera" }), _jsx("span", { children: "Camera" })] })] }), !props.value && cameraOpen && (_jsxs(CameraRoot, { className: "absolute w-full h-full z-1", format: "image/png", onCapture: (file) => {
                    props.onChange(file);
                    setCameraOpen(false);
                }, facingMode: props.facingMode, children: [_jsx(CameraShutterButton, {}), _jsx(CameraDeviceSelector, {}), _jsx(CameraFullscreenButton, {}), _jsx(Button, { onClick: () => setCameraOpen(false), color: "ghost", size: "small", className: "text-white absolute top-2 left-2", children: "Cancel" })] })), _jsx(ImageUploaderRemoveButton, {})] })));
}
export function ImageUploaderFileButton(_a) {
    var { children } = _a, props = __rest(_a, ["children"]);
    const { inputId, dragging } = useUploaderContext();
    const [focused, setFocused] = useState(false);
    useEffect(() => {
        function updateFocus(ev) {
            setFocused(ev.target === document.activeElement);
        }
        setTimeout(() => {
            const input = document.getElementById(inputId);
            if (input) {
                input.addEventListener('focus', updateFocus);
                input.addEventListener('blur', updateFocus);
            }
        }, 0);
        return () => {
            const input = document.getElementById(inputId);
            if (input) {
                input.removeEventListener('focus', updateFocus);
                input.removeEventListener('blur', updateFocus);
            }
        };
    }, [inputId]);
    return (_jsx(Button, Object.assign({ color: "ghost", asChild: true, visuallyFocused: focused }, props, { children: _jsx("label", { htmlFor: inputId, children: children !== null && children !== void 0 ? children : (_jsxs(_Fragment, { children: [_jsx(Icon, { name: "upload" }), _jsx("span", { children: dragging ? 'Drop' : 'Upload' })] })) }) })));
}
export function ImageUploaderRemoveButton(_a) {
    var { className } = _a, rest = __rest(_a, ["className"]);
    const { value, onChange } = useUploaderContext();
    if (!value)
        return null;
    return (_jsx(Button, Object.assign({ color: "ghost", size: "icon", className: clsx('layer-variants:absolute layer-variants:top-2 layer-variants:right-2 layer-variants:w-32px layer-variants:h-32px layer-variants:border-none layer-variants:p-2 layer-variants:cursor-pointer layer-variants:bg-white layer-variants:color-black layer-variants:rounded-lg layer-variants:transition-colors layer-variants:shadow-sm layer-variants:z-10', className), onClick: () => onChange(null) }, rest, { children: _jsx(Icon, { name: "x" }) })));
}
export function ImageUploaderDisplay(_a) {
    var { className } = _a, rest = __rest(_a, ["className"]);
    const { value } = useUploaderContext();
    return value ? (_jsx("img", Object.assign({ src: value, className: clsx('layer-components:w-full layer-components:h-full layer-components:object-cover layer-components:object-center layer-components:rounded-lg', className) }, rest))) : null;
}
export function ImageUploaderEmptyControls({ children, className, }) {
    const { value, draggingOver } = useUploaderContext();
    if (value)
        return null;
    return (_jsx("div", { className: classNames('layer-components:absolute layer-components:inset-0 layer-components:flex layer-components:flex-col layer-components:items-center layer-components:justify-center layer-components:gap-3 layer-components:rounded-lg', {
            'layer-components:bg-[rgba(0,0,0,0.05)]': !draggingOver,
            'layer-components:bg-[rgba(0,0,0,0.1)]': draggingOver,
        }, className), children: children }));
}
export const ImageUploader = Object.assign(ImageUploaderPrebuilt, {
    Root: ImageUploaderRoot,
    FileButton: ImageUploaderFileButton,
    RemoveButton: ImageUploaderRemoveButton,
    Display: ImageUploaderDisplay,
    EmptyControls: ImageUploaderEmptyControls,
});
//# sourceMappingURL=ImageUploader.js.map